import { Avatar, Box, Typography, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useCallback, useState } from 'react';
import { FaKey as KeyIcon } from 'react-icons/fa';
import ButtonGroup from '../../common/components/ButtonGroup';
import ModalDialog from '../../common/components/ModalDialog';
import { getLocalTime, getOneYearFromNow } from '../utils/date-utils';
import {
  ApiKey,
  ApiKeyStatus,
  SetApiKeyExpirationInput,
} from '../types/api-key-types';
import { apiKeyTypeMap, getExpirationTimeSec } from '../utils/api-key-utils';
import ApiKeyDatePicker from './ApiKeyDatePicker';

const SetApiKeyExpirationModal: FC<
  React.PropsWithChildren<SetApiKeyExpirationModalProps>
> = props => {
  const theme = useTheme();
  const { open, onClose, apiKey, onSetExpiration, isLoading } = props;
  const [date, setDate] = useState<Dayjs | null>(dayjs(getOneYearFromNow()));

  const onSave = useCallback(() => {
    if (!date) {
      return;
    }

    const input: SetApiKeyExpirationInput = {
      id: apiKey.id,
      expiresInSec: getExpirationTimeSec(date),
    };
    onSetExpiration(input);
  }, [onSetExpiration, apiKey, date]);

  return (
    <ModalDialog
      modalColor={theme.palette.info.main}
      aria-label='Set API Key Expiration'
      open={open}
      onClose={onClose}
      modalHeader={`Set API Key Expiration`}>
      <Box flexGrow={1} mb={2}>
        <Typography variant='body2'>
          {`Select new expiration time for the following API Key`}
        </Typography>
        <Box display='flex' alignItems='center' mt={2} mb={2} padding={1}>
          <Avatar>
            <KeyIcon />
          </Avatar>
          <Box display='flex' flexDirection='column' ml={1}>
            <Typography variant='body2' color='text.primary'>
              {apiKey.name}
            </Typography>
            <Typography variant='body2' color='text.primary'>
              {apiKeyTypeMap[apiKey.apiKeyRoleType]}
            </Typography>
          </Box>
        </Box>
        {apiKey.apiKeyStatus === ApiKeyStatus.Expired && (
          <Typography variant='body2' color='text.primary' marginBottom={2}>
            Note: token expired at {getLocalTime(apiKey.exp)}
          </Typography>
        )}
        {apiKey.apiKeyStatus === ApiKeyStatus.Disabled && (
          <Typography variant='body2' color='text.primary' marginBottom={2}>
            Note: token is disabled, you will need to enable it in order to use
            it in your application
          </Typography>
        )}
        <ApiKeyDatePicker
          title={'Set New Expiration Date'}
          date={date}
          setDate={setDate}
        />
      </Box>
      <ButtonGroup
        onCancel={onClose}
        onClick={onSave}
        isLoading={isLoading}
        buttonCancelLabel='Cancel'
        buttonCancelTooltip='Cancel this operation'
        buttonActionLabel='Save'
        buttonActionTooltip='Set new expiration date'
        buttonActionColor='primary'
        isActionDisabled={false}
      />
    </ModalDialog>
  );
};

interface SetApiKeyExpirationModalProps {
  open: boolean;
  apiKey: ApiKey;
  onClose: () => void;
  onSetExpiration: (input: SetApiKeyExpirationInput) => void;
  isLoading: boolean;
}

export default SetApiKeyExpirationModal;
